import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { fetchUserProfile } from "@/services/api";
import { openSubscriptionCheckout } from "@/services/paddle";
import toast from "react-hot-toast";

let checkoutLaunchSignature: string | null = null;

export function CheckoutPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<"loading" | "error">("loading");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const priceId = searchParams.get("price_id")?.trim() || "";
  const userEmailParam = searchParams.get("user_email")?.trim() || "";

  useEffect(() => {
    if (!priceId) {
      setErrorMessage("Missing price_id. Add it to the URL, for example: /checkout?price_id=…&user_email=…");
      setStatus("error");
      return;
    }

    const signature = `${priceId}|${userEmailParam}`;

    const run = async () => {
      if (checkoutLaunchSignature === signature) {
        return;
      }
      checkoutLaunchSignature = signature;

      try {
        const profile = await fetchUserProfile();

        let decodedParamEmail = "";
        if (userEmailParam) {
          try {
            decodedParamEmail = decodeURIComponent(
              userEmailParam.replace(/\+/g, " ")
            );
          } catch {
            checkoutLaunchSignature = null;
            setErrorMessage("Invalid user_email in the URL.");
            setStatus("error");
            return;
          }
        }

        if (decodedParamEmail) {
          const same =
            profile.email.toLowerCase() === decodedParamEmail.toLowerCase();
          if (!same) {
            checkoutLaunchSignature = null;
            setErrorMessage(
              "This checkout link is for a different account. Sign in with the email from the link, then try again."
            );
            setStatus("error");
            toast.error("Email does not match this checkout link.");
            return;
          }
        }

        await openSubscriptionCheckout(
          profile.id,
          profile.name || profile.email.split("@")[0] || "Customer",
          profile.email,
          priceId
        );
      } catch (e) {
        checkoutLaunchSignature = null;
        console.error("Checkout launch failed:", e);
        setErrorMessage("Could not open checkout. Please try again from your subscription page.");
        setStatus("error");
        toast.error("Could not open checkout.");
      } finally {
        setTimeout(() => {
          if (checkoutLaunchSignature === signature) {
            checkoutLaunchSignature = null;
          }
        }, 3000);
      }
    };

    run();
  }, [priceId, userEmailParam]);

  if (status === "error" && errorMessage) {
    return (
      <div className="min-h-screen bg-gray-900 flex flex-col items-center justify-center px-4 text-center">
        <p className="text-gray-200 max-w-md mb-6">{errorMessage}</p>
        <button
          type="button"
          onClick={() => navigate("/profile?active=subscription")}
          className="text-purple-400 hover:text-purple-300 underline"
        >
          Go to subscription
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 flex flex-col items-center justify-center gap-4">
      <div className="h-10 w-10 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
      <p className="text-gray-400 text-sm">Opening checkout…</p>
    </div>
  );
}
